/**
 * Settings tab (docs/plans/oncall-line-plan.md 2.3, 4, 5, 7.2, 7.5, 7.7, 11
 * step 5; WP-11). Line admins only: OncallApp routes /oncall/settings here
 * behind its admin guard, and PATCH /line and POST /line carry
 * requireOncallAdmin on the server.
 *
 * For the site in the header (switching site switches line):
 *
 *   - no line yet (GET /line answers null / 404 ONCALL_NO_LINE): the form
 *     that creates it (components/settings/CreateLineForm.tsx, POST /line)
 *   - a line: its settings (components/settings/LineSettingsForm.tsx): the
 *     number (read-only), the name, the hunt settings with the live
 *     worst-case wait (huntMath.worstCaseSeconds, the port of
 *     hunt.worstCaseSeconds), the spoken texts, the alert emails and the
 *     fallback Bin's listed phones
 *   - health beside it (components/settings/HealthPanel.tsx, GET /health):
 *     the switches, the Twilio account, the last heartbeat, active members
 *
 * GET /line and GET /health load in parallel; a failed health read shows its
 * own Retry and never blocks the settings. No SSE and no poll: the page reads
 * on load, on Refresh and after a save. Default export, no required props.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader, { RefreshButton } from '../../ui/PageHeader';
import { useToast } from '../../ui/Toast';
import { secondaryBtnCls } from '../../ui/styles';
import { apiErrorMessage, getLine, getOncallHealth } from '../api/oncallApi';
import type { OncallHealth, OncallLine } from '../api/oncallApi';
import CreateLineForm from '../components/settings/CreateLineForm';
import HealthPanel from '../components/settings/HealthPanel';
import LineSettingsForm from '../components/settings/LineSettingsForm';

type LineState =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'ready'; line: OncallLine }
  | { kind: 'error'; message: string };

export default function SettingsTab() {
  const { currentSite } = useAuth();
  const { toast } = useToast();
  const siteId = currentSite?.id ?? null;
  const timeZone = currentSite?.timezone ?? null;

  const [lineState, setLineState] = useState<LineState>({ kind: 'loading' });
  const [health, setHealth] = useState<OncallHealth | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Answers for a site that is no longer current are dropped.
  const siteRef = useRef(siteId);
  siteRef.current = siteId;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const loadLine = useCallback(async () => {
    const forSite = siteRef.current;
    if (!forSite) return;
    try {
      const line = await getLine();
      if (!mounted.current || siteRef.current !== forSite) return;
      setLineState(line ? { kind: 'ready', line } : { kind: 'none' });
    } catch (err) {
      if (!mounted.current || siteRef.current !== forSite) return;
      setLineState({ kind: 'error', message: apiErrorMessage(err, "This site's line could not be loaded.") });
    }
  }, []);

  const loadHealth = useCallback(async () => {
    const forSite = siteRef.current;
    if (!forSite) return;
    try {
      const h = await getOncallHealth();
      if (!mounted.current || siteRef.current !== forSite) return;
      setHealth(h);
      setHealthError(null);
    } catch (err) {
      if (!mounted.current || siteRef.current !== forSite) return;
      setHealth(null);
      setHealthError(apiErrorMessage(err, 'The line\'s health could not be loaded.'));
    }
  }, []);

  useEffect(() => {
    if (!siteId) return;
    setLineState({ kind: 'loading' });
    setHealth(null);
    setHealthError(null);
    loadLine();
    loadHealth();
  }, [siteId, loadLine, loadHealth]);

  const refresh = async () => {
    setRefreshing(true);
    await Promise.all([loadLine(), loadHealth()]);
    if (mounted.current) setRefreshing(false);
  };

  let main: JSX.Element;
  if (lineState.kind === 'loading') {
    main = (
      <div className="flex justify-center py-12">
        <div role="status" aria-label="Loading" className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary-600 dark:border-primary-400" />
      </div>
    );
  } else if (lineState.kind === 'error') {
    main = (
      <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
        <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">{lineState.message}</span>
        <button
          type="button"
          onClick={() => {
            setLineState({ kind: 'loading' });
            loadLine();
          }}
          className={secondaryBtnCls}
        >
          Retry
        </button>
      </div>
    );
  } else if (lineState.kind === 'none') {
    main = (
      <CreateLineForm
        siteName={currentSite?.name}
        onCreated={(line) => {
          toast(`${line.name} is created. Add its team next.`, { type: 'success' });
          setLineState({ kind: 'ready', line });
          loadHealth();
        }}
        onExists={() => {
          toast('This site already has a line. It has been loaded.', { type: 'info' });
          setLineState({ kind: 'loading' });
          loadLine();
          loadHealth();
        }}
      />
    );
  } else {
    main = (
      <LineSettingsForm
        line={lineState.line}
        activeMembers={health ? health.active_members : null}
        onSaved={(line) => {
          setLineState({ kind: 'ready', line });
          loadHealth();
        }}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1700px] space-y-4 px-4 pt-4 pb-6 sm:px-6 lg:px-8 lg:pt-5" data-testid="oncall-settings-tab">
      <PageHeader
        title="Settings"
        icon={SlidersHorizontal}
        subtitle={
          currentSite?.name
            ? `${currentSite.name}'s line: its number, what it says, how it rings, and its health.`
            : "This site's line: its number, what it says, how it rings, and its health."
        }
        actions={<RefreshButton onClick={refresh} loading={refreshing} label="Reload the settings" />}
      />
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,400px)]">
        <div className="min-w-0">{main}</div>
        <div className="min-w-0 xl:sticky xl:top-4 xl:self-start">
          <HealthPanel
            health={health}
            error={healthError}
            timeZone={timeZone}
            onRetry={() => {
              setHealthError(null);
              loadHealth();
            }}
          />
        </div>
      </div>
    </div>
  );
}
